import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../api'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { safeAdminError } from '../safe-ui'
import { ADMIN_TOKEN_KEY } from '../types'
import { apiUrl } from '../../lib/api-base'
import { getAuthItem } from '../../shared/authStorage'

type BackupRow = {
  id: string
  sizeBytes?: number
  createdAt?: string | null
  label?: string
  scope?: string
  sha256?: string
  includes?: string[]
}

type BackupStatus = {
  enabled?: boolean
  ready?: boolean
  scriptReady?: boolean
  sudoReady?: boolean
  retain?: number
  backupDir?: string
}

function formatBytes(n?: number) {
  if (!n || n <= 0) return '—'
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatWhen(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

export function AdminSystemBackupPage() {
  const [status, setStatus] = useState<BackupStatus | null>(null)
  const [backups, setBackups] = useState<BackupRow[]>([])
  const [label, setLabel] = useState('manual')
  const [scope, setScope] = useState<'full-vps' | 'perfectpredict'>('full-vps')
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    setError('')
    const res = await adminApi.listSystemBackups()
    setStatus(res.status)
    setBackups(res.backups || [])
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await refresh()
      } catch (err) {
        if (!cancelled) setError(safeAdminError(err, 'Unable to load backups'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refresh])

  async function onCreate() {
    setBusy(true)
    setError('')
    setNote('')
    try {
      const res = await adminApi.createSystemBackup(label.trim() || 'manual', scope)
      setNote(`Backup ready (${res.backup?.scope || scope}): ${res.backup?.id || 'created'}`)
      await refresh()
    } catch (err) {
      setError(safeAdminError(err, 'Backup failed'))
    } finally {
      setBusy(false)
    }
  }

  async function onDelete(id: string) {
    if (!window.confirm(`Delete backup ${id}?`)) return
    setBusy(true)
    setError('')
    try {
      await adminApi.deleteSystemBackup(id)
      setNote(`Deleted ${id}`)
      await refresh()
    } catch (err) {
      setError(safeAdminError(err, 'Delete failed'))
    } finally {
      setBusy(false)
    }
  }

  function onDownload(id: string) {
    const token = getAuthItem(ADMIN_TOKEN_KEY)
    if (!token) {
      setError('Admin session expired')
      return
    }
    // Stream download via navigation (no blob) — whole-VPS archives are hundreds of MB.
    setError('')
    setNote(`Downloading ${id}…`)
    const url = apiUrl(
      `/api/admin/system/backups/${encodeURIComponent(id)}?access_token=${encodeURIComponent(token)}`,
    )
    const a = document.createElement('a')
    a.href = url
    a.download = id
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  return (
    <div className="ap-page">
      <PageHeader eyebrow="Operations" title="System Backup">
        <p className="cp-muted">
          Full restorable snapshots of Perfect Predict (app, data, www, nginx, TLS).
        </p>
      </PageHeader>

      {error ? (
        <StateBlock tone="error" title="Error">
          {error}
        </StateBlock>
      ) : null}
      {note ? (
        <StateBlock tone="info" title="Status">
          {note}
        </StateBlock>
      ) : null}

      <section className="pp-card-stack" style={{ marginBottom: '1.25rem' }}>
        <div className="pp-mobile-card">
          <div className="pp-mobile-card__row">
            <span>Backup engine</span>
            <strong>{status?.ready ? 'Ready' : status?.enabled === false ? 'Disabled' : 'Not configured'}</strong>
          </div>
          <div className="pp-mobile-card__row">
            <span>Retention</span>
            <strong>{status?.retain ?? '—'} archives</strong>
          </div>
          <div className="pp-mobile-card__row">
            <span>Storage</span>
            <strong style={{ wordBreak: 'break-all' }}>{status?.backupDir || '—'}</strong>
          </div>
          <div className="pp-mobile-card__row">
            <span>Script / sudo</span>
            <strong>
              {status?.scriptReady ? 'script ok' : 'script missing'} · {status?.sudoReady ? 'sudo ok' : 'sudo missing'}
            </strong>
          </div>
        </div>
      </section>

      <section className="cp-panel" style={{ marginBottom: '1.25rem' }}>
        <div className="cp-panel__head">
          <h2>Create backup</h2>
        </div>
        <div className="ap-filters" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
          <label className="cp-field">
            Scope
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as 'full-vps' | 'perfectpredict')}
              disabled={busy || !status?.ready}
            >
              <option value="full-vps">Whole VPS (all apps / sites / DBs)</option>
              <option value="perfectpredict">Perfect Predict only</option>
            </select>
          </label>
          <label className="cp-field">
            Label
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="manual"
              maxLength={40}
              disabled={busy || !status?.ready}
            />
          </label>
          <button
            type="button"
            className="pp-btn pp-btn--primary"
            disabled={busy || !status?.ready}
            onClick={() => void onCreate()}
          >
            {busy ? 'Working…' : scope === 'full-vps' ? 'Run whole VPS backup' : 'Run PP backup'}
          </button>
        </div>
        <p className="cp-muted" style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
          Default / daily timer = <strong>whole VPS</strong> (Infinity, LeadOps, Perfect Predict, nginx, TLS,
          Postgres dumps, Docker volumes). Not a raw disk image of the OS. Restore is CLI-only.
        </p>
      </section>

      <section className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <div className="cp-panel__head">
          <h2>Archives</h2>
          <button type="button" className="pp-btn pp-btn--secondary pp-btn--sm" disabled={busy} onClick={() => void refresh()}>
            Refresh
          </button>
        </div>
        {loading ? (
          <p className="cp-muted ap-inline-status" aria-live="polite">
            Loading…
          </p>
        ) : null}
        {!loading && backups.length === 0 ? <div className="pp-empty">No backups yet.</div> : null}
        {backups.length > 0 || loading ? (
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Scope</th>
                  <th>Label</th>
                  <th>Size</th>
                  <th>Archive</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && backups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="cp-muted">
                      Loading…
                    </td>
                  </tr>
                ) : null}
                {backups.map((b) => (
                  <tr key={b.id}>
                    <td>{formatWhen(b.createdAt)}</td>
                    <td>{b.scope === 'full-vps' ? 'Whole VPS' : b.scope === 'perfectpredict' ? 'PP only' : b.scope || '—'}</td>
                    <td>{b.label || '—'}</td>
                    <td>{formatBytes(b.sizeBytes)}</td>
                    <td>
                      <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{b.id}</code>
                      {b.sha256 ? (
                        <div className="cp-muted" style={{ fontSize: '0.7rem' }}>
                          sha256 {b.sha256.slice(0, 12)}…
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        <button
                          type="button"
                          className="pp-btn pp-btn--secondary pp-btn--sm"
                          disabled={busy}
                          onClick={() => onDownload(b.id)}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          className="pp-btn pp-btn--secondary pp-btn--sm"
                          disabled={busy}
                          onClick={() => void onDelete(b.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  )
}
